<template>
  <Container
    v-if="links?.length > 0 && (links?.[0]?.slug || links?.[0]?.url)"
    id="links"
    class="mb-10 md:mb-15 xl:mb-17.5"
  >
    <div
      class="bp1680:max-w-[1054px] mx-auto flex w-full max-w-185 flex-col gap-3.75 lg:max-w-[778px] xl:max-w-[894px] 2xl:max-w-[1054px]"
    >
      <h2
        v-if="title"
        class="bp1680-text-23 font-header leading-[120%] text-gray-medium md:text-[17px] xl:text-xl"
      >
        {{ title }}
      </h2>

      <div class="flex flex-col">
        <template v-for="(link, index) in visibleLinks" :key="index">
          <NuxtLink
            :to="
              link.type === 'file'
                ? link.url
                : localePath(link.url || undefined)
            "
            :target="(link.type === 'file' ? '_blank' : link.target) || '_self'"
            :download="shouldForceDownload(link) ? '' : undefined"
            rel="noopener noreferrer"
            :class="[
              'translate-color flex items-center justify-between gap-4 border-b border-gray py-3.75 duration-300 first:border-t hover:text-gray-medium',
              link.type === 'file'
                ? 'bp1680:text-[23px]/[28px] text-[16px]/[19px] md:text-[17px]/[21px] lg:text-[17px]/[21px] xl:text-[20px]/[24px] 2xl:text-[23px]/[28px]'
                : 'xl:text-xl/[115%]',
              { 'border-b': index < 4 },
            ]"
            :aria-label="link.title"
          >
            <span>{{ link.title }}</span>

            <DownloadIcon
              v-if="link.type === 'file'"
              class="mr-4 h-4.5 w-4.5 shrink-0 duration-300 group-hover:text-gray-medium"
            />
          </NuxtLink>
        </template>

        <!-- Приховані лінки (Show More) -->
        <div
          v-if="hiddenLinks?.length > 0"
          class="grid transition-all duration-400 ease-in-out"
          :class="
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          "
        >
          <div class="flex flex-col overflow-hidden">
            <template v-for="(link, index) in hiddenLinks" :key="index">
              <NuxtLink
                :to="
                  link.type === 'file'
                    ? link.url
                    : localePath(link.url || undefined)
                "
                :target="
                  (link.type === 'file' ? '_blank' : link.target) || '_self'
                "
                :download="shouldForceDownload(link) ? '' : undefined"
                rel="noopener noreferrer"
                :class="[
                  'translate-color flex items-center justify-between gap-4 border-b border-gray py-3.75 duration-300 hover:text-gray-medium',
                  link.type === 'file'
                    ? 'bp1680:text-[23px]/[28px] text-[16px]/[19px] md:text-[17px]/[21px] lg:text-[17px]/[21px] xl:text-[20px]/[24px] 2xl:text-[23px]/[28px]'
                    : 'xl:text-xl/[115%]',
                  { 'border-b': index < 4 },
                ]"
              >
                <span>{{ link.title }}</span>

                <DownloadIcon
                  v-if="link.type === 'file'"
                  class="mr-4 h-4.5 w-4.5 shrink-0 duration-300 group-hover:text-gray-medium"
                />
              </NuxtLink>
            </template>
          </div>
        </div>
      </div>

      <ShowMoreButton
        v-if="links?.length > 5"
        v-model:is-expanded="isExpanded"
        button-class="md:pt-3.75"
      />
    </div>
  </Container>
</template>

<script setup lang="ts">
interface LinkItem {
  color?: string | null
  title: string
  slug?: string | null
  url?: string | null
  type: string
  target: string
}

interface Props {
  title?: string
  links: LinkItem[]
}

const props = defineProps<Props>()
const localePath = useLocalePath()
const isExpanded = ref(false)

const visibleLinks = computed(() => props.links?.slice(0, 5) ?? [])
const hiddenLinks = computed(() => props.links?.slice(5) ?? [])

const shouldForceDownload = (link: LinkItem) => {
  if (link.type !== 'file') return false

  const urlStr = link.url ? link.url.toLowerCase() : ''

  const isPdf = urlStr.includes('.pdf')

  return !isPdf
}
</script>
